import React, { useState } from 'react';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { Button, DatePicker, fetch, Form, message, Modal } from '@maxtropy/components';
import { apiV2EnergyResilienceAnalysisRerunPost } from '@maxtropy/device-customer-apis-v2';
import styles from './index.module.scss';

const { RangePicker } = DatePicker;
const dateFormat = 'YYYY-MM-DD';

type RangeValue = [Dayjs | null, Dayjs | null] | null;

interface RecalculateModalProps {
  open: boolean;
  onCancel: () => void;
  onSuccess: () => void;
}

const RecalculateModal: React.FC<RecalculateModalProps> = ({ open, onCancel, onSuccess }) => {
  const [form] = Form.useForm();
  const [value, setValue] = useState<RangeValue>(null);
  const [dates, setDates] = useState<RangeValue>();
  const [submitting, setSubmitting] = useState(false);

  const disabledDate = (current: Dayjs) => {
    if (!dates) {
      return false;
    }
    const tooLate = dates[0] && current.diff(dates[0], 'months') >= 6;
    const tooEarly = dates[1] && dates[1].diff(current, 'months') >= 6;
    const afterToday = current && current > dayjs().endOf('day');
    return !!tooEarly || !!tooLate || !!afterToday;
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      setDates([null, null]);
    } else {
      setDates(null);
    }
  };

  const reset = () => {
    form.resetFields();
    setValue(null);
    setDates(null);
  };

  const handleCancel = () => {
    reset();
    onCancel();
  };

  const handleSubmit = async () => {
    const values = await form.validateFields();
    const [startDate, endDate] = values.date;
    setSubmitting(true);
    apiV2EnergyResilienceAnalysisRerunPost({
      startTime: dayjs(startDate).startOf('day').valueOf(),
      endTime: dayjs(endDate).endOf('day').valueOf(),
    })
      .then(() => {
        message.success('重计算已提交');
        reset();
        onCancel();
        onSuccess();
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <Modal
      title="重计算"
      forceRender
      className={styles.calcModalBox}
      open={open}
      afterClose={reset}
      onCancel={handleCancel}
      contentClassName="modal-form-content"
      footer={[
        <Button key="back" onClick={handleCancel} className={styles.cancelBtn}>
          取消
        </Button>,
        <Button key="submit" type="primary" loading={submitting} onClick={handleSubmit}>
          确定
        </Button>,
      ]}
    >
      <div className={styles.modalBody}>
        <div className={styles.tipWrapper}>
          <ExclamationCircleOutlined style={{ color: 'var(--mx-warning-color)', fontSize: 12 }} />
          <span className={styles.txt}>重计算数值会覆盖现有数据</span>
        </div>

        <Form form={form}>
          <Form.Item label="选择时间" name="date" rules={[{ required: true, message: '请选择时间' }]}>
            <RangePicker
              value={value || dates}
              disabledDate={disabledDate}
              onCalendarChange={setDates}
              onChange={setValue}
              onOpenChange={handleOpenChange}
            />
          </Form.Item>
        </Form>
      </div>
    </Modal>
  );
};

export default RecalculateModal;
